import { CustomerEntity } from "../../Objects/Customer/CustomerEntity"
import { AwardManagerEntity } from "../../Objects/Awards/AwardManagerEntity"
import { ICartItem } from "../../Interfaces/ICardItem"
import { IAddtionalRequest } from "../../Interfaces/IAddtionalRequest"
import { EEnableStatusType } from "../../Enum/EEnableStatusType"
import { ApplyAwardDiscounts } from "../../Objects/Calculates/ApplyAwardDiscounts"

export interface IAppliedAward {
  awardManager: AwardManagerEntity
  discount: number
}

export interface ICartSummary {
  subtotal: number
  grossTotal: number
  savings: number
  awards: IAppliedAward[]
  totalDiscount: number
  total: number
}

export const CalculateCartSummary = (cart: ICartItem[], customer?: CustomerEntity): ICartSummary => {
  const subtotal = cart.reduce((sum, item) => sum + getItemTotal(item), 0)

  const savings = cart.reduce((sum, item) => sum + getItemSavings(item), 0)

  const availableAwards =
    customer?.awardManagers?.filter(
      (a) =>
        a.status === EEnableStatusType.Active && a.hasAwardToTake && a.awardEntity?.status === EEnableStatusType.Active,
    ) ?? []

  const appliedAwards: IAppliedAward[] = ApplyAwardDiscounts({
    candidates: availableAwards
      .filter((awardManager) => Boolean(awardManager.awardEntity))
      .map((awardManager) => ({ source: awardManager, award: awardManager.awardEntity! })),
    initialTotal: subtotal,
  }).map((applied) => ({ awardManager: applied.source, discount: applied.discount }))

  const totalDiscount = appliedAwards.reduce((sum, a) => sum + a.discount, 0)

  const remainingTotal = subtotal - totalDiscount

  const grossTotal = cart.reduce((sum, item) => sum + getItemGrossTotal(item), 0)

  return {
    subtotal,
    savings,
    awards: appliedAwards,
    grossTotal,
    totalDiscount,
    total: Math.max(0, remainingTotal),
  }
}

export const getItemGrossTotal = (item: ICartItem) => {
  const basePrice = item.oldPrice && item.oldPrice > item.price ? item.oldPrice : item.price

  return (basePrice + getAddOnsTotal(item.selectedAddOns, item.freeAddtionalsLimit) + getSelectionsTotal(item.selectedSelections ?? [])) * item.quantity
}


const resolvePaidAddOnsTotal = (addOns: IAddtionalRequest[], freeAddtionalsLimit: number = 0) => {
  let freeRemaining = Math.max(0, freeAddtionalsLimit)

  return addOns.reduce((sum, addOn) => {
    const quantity = addOn.quantity ?? 0

    if (!addOn.isFreeEligible || freeRemaining <= 0) {
      return sum + addOn.price * quantity
    }

    const freeQuantity = Math.min(quantity, freeRemaining)
    freeRemaining -= freeQuantity

    return sum + addOn.price * (quantity - freeQuantity)
  }, 0)
}

export const getCartTotal = (cart: ICartItem[]) => cart.reduce((sum, item) => sum + getItemTotal(item), 0)

export const getCartSavings = (cart: ICartItem[]) => cart.reduce((sum, item) => sum + getItemSavings(item), 0)

export const getAddOnsTotal = (addOns: IAddtionalRequest[], freeAddtionalsLimit: number = 0) =>
  resolvePaidAddOnsTotal(addOns, freeAddtionalsLimit)

export const getItemTotal = (item: ICartItem) => getItemUnitPrice(item) * item.quantity

export const getItemUnitPrice = (
  item: Pick<ICartItem, "price" | "selectedAddOns" | "freeAddtionalsLimit" | "selectedSelections">,
) => item.price + getAddOnsTotal(item.selectedAddOns, item.freeAddtionalsLimit) + getSelectionsTotal(item.selectedSelections ?? [])

export const getSelectionsTotal = (selections: { value: number }[]) => selections.reduce((sum, item) => sum + item.value, 0)

export const getItemSavings = (item: ICartItem) => {
  if (!item.oldPrice || item.oldPrice <= item.price) {
    return 0
  }

  return (item.oldPrice - item.price) * item.quantity
}
